import { getCSRFToken } from '../utils/getCSRFToken';
import {
  getTodayISO,
  formatDateISO,
  formatDateDDMMYYYY,
  parseDate,
} from '../utils/dateUtils';

export default function registerRateSettingsStore() {
  Alpine.store('rateSettingsStore', {
    calendarSelected: '',
    calendarSlug: '',
    currentDate: '',
    currency: 'EUR', // Default to EUR if no Currency found
    tempDate: '', // Temporary date for holding the selection
    flatpickrInstance: null, // For bulk update date range picker
    singleDatePickerInstance: null, // For navigation date picker
    rateSet: {
      id: '',
      public_name: '',
      internal_title: '',
      main_image: '',
      is_fixed: false,
      date_select: '',
      tour_duration_mode: 'both_single_default',
      preset_durations: [], // Array of {id, duration, name, order, is_default}
      people_per_unit: 0,
      person_label_singular: 'Person',
      person_label_plural: 'People',
      accommodation_type: null,
      tour_type: null,
      calendar: '',
      created_at: '',
      description: '',
      max_los: 0,
      min_los: 0,
      order: 0,
      people_influence_price: false,
      use_person_types: false,
      private: false,
      published: false,
      store_pages: [],
      parent: null,
      tax_fees: [],
      tour_type: null,
      updated_at: '',
      closed_to_arrival: [],
      closed_to_departure: [],
      related_ratesets: [],
      associated_addons: [],
    },
    availabilityRates: [
      {
        label: '',
        rates: [
          {
            date: '',
            end_date: '',
            price: 0,
            min_length: null,
            max_length: null,
            closed_to_arrival: false,
            closed_to_departure: false,
            people: 0,
          },
        ],
      },
    ],
    originalValue: '',
    rateType: '',
    rateUnitType: '',
    isLoading: false,
    isEditing: false,
    isInitializing: true,
    isProgrammaticDateChange: false, // Flag to prevent date picker onChange during programmatic updates
    toastStore: Alpine.store('toastStore'),
    // Bulk form state
    bulkForm: {
      startDate: null,
      endDate: null,
      price: null,
      prices: [],
      min_length: null,
      max_length: null,
      closed_to_arrival: [],
      closed_to_departure: [],
      instant_book_enabled: [],
      is_blocked: false,
      price_active: false,
      prices_active: false,
      min_length_active: false,
      max_length_active: false,
      closed_to_arrival_active: false,
      closed_to_departure_active: false,
      instant_book_enabled_active: false,
      is_blocked_active: false,
    },
    newFixedInterval: {
      dateRange: '',
      price: null,
      prices: [],
    },
    availableTaxes: [],
    availableFees: [],
    availableDiscounts: [],
    // Listing custom fields
    availableListingFields: [],
    listingFieldAssignments: [],
    isSavingListingFieldAssignments: false,
    maxListingCustomFields: 2,
    showNewTaxFeeModal: false,
    newTaxFee: {
      calendar: '',
      name: '',
      amount: 0,
      type: 'PERCENTAGE',
      is_tax: true,
      is_inclusive: false,
      applies_to_fees: false,
    },
    // Track pending changes for batch save
    pendingChanges: {}, // { rateId: { field: value, ... } }
    hasPendingChanges: false,
    // Related services functionality (ratesets and store pages)
    availableRatesets: [],
    availableStorePages: [],
    allAvailableItems: [],
    selectedRelatedRatesets: [],
    selectedRelatedStorePages: [],
    relatedServicesLoading: false,
    allows6RelatedRatesets: false, // Calendar setting for 6 vs 3 related services
    filteredStorePages: [], // Store pages for location dropdown
    // Add-ons functionality
    availableAddons: [],
    selectedAddons: [],
    addonsLoading: false,
    servicesAndAddonsLoaded: false, // Flag to track if services and addons have been loaded
    // Package structure management
    packageComponents: [],
    primaryComponent: null,
    secondaryComponents: [],
    packageExtras: [],
    availablePackageExtras: [],
    packageLoading: false,
    editingComponent: null,
    editingRevenueAllocation: false,
    totalRevenueAllocation: 0,

    init() {
      // Set currentDate to the user's local date (timezone-safe)
      this.currentDate = getTodayISO(); // Get today's date in YYYY-MM-DD format

      // Warn user before leaving page if there are unsaved changes
      const store = this;
      window.addEventListener('beforeunload', (e) => {
        if (store.hasPendingChanges) {
          // Modern browsers ignore custom messages, but we still need to set returnValue
          e.preventDefault();
          e.returnValue = ''; // Chrome requires returnValue to be set
          return ''; // Some browsers require a return value
        }
      });
    },

    async loadStaticData() {
      // Load static data (rate_set, taxes, fees, discounts) - called once on initial load
      console.log('Loading static rate settings data...');
      const params = new URLSearchParams();
      if (this.rateSet && this.rateSet.id) {
        params.append('rate', this.rateSet.id);
      }
      const url = `/rates/load-rate-settings-data?${params.toString()}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        this.rateSet = data.rate_set;
        this.normalizePersonLabels();
        // Convert parent field to string to match option values
        if (this.rateSet.parent !== null && this.rateSet.parent !== undefined) {
          this.rateSet.parent = String(this.rateSet.parent);
        } else {
          this.rateSet.parent = '';
        }
        this.rateType = data.rate_type;
        this.rateUnitType = data.rate_unit_type;
        this.availableTaxes = data.available_taxes;
        this.availableFees = data.available_fees;
        this.availableDiscounts = data.available_discounts || [];

        // Load package components if this is a package
        if (this.rateSet.is_package) {
          await this.loadPackageComponents();
          await this.loadPackageExtras();
          await this.loadAvailablePackageExtras();
        }

        // Initialize related services data
        this.selectedRelatedRatesets = this.rateSet.related_ratesets || [];
        this.selectedRelatedStorePages = this.rateSet.related_store_pages || [];

        // Load services and addons (only called once)
        if (!this.servicesAndAddonsLoaded) {
          this.loadAvailableServices();
          this.loadAvailableAddons();
          this.servicesAndAddonsLoaded = true;
        }

        // Initialize add-ons data
        this.selectedAddons = this.rateSet.associated_addons || [];
        this.newFixedInterval.prices = Array.from(
          { length: this.rateSet.people_per_unit },
          () => null,
        );
      } catch (error) {
        console.error('Failed to fetch static rate settings data:', error);
        this.error = error.message;
        throw error;
      }
    },

    async loadAvailabilityRates(skipLoadingState = false) {
      // Load availability rates for current date - called on initial load and date navigation
      // skipLoadingState: if true, don't manage isLoading state (used when called from rateSetData)

      if (!skipLoadingState) {
        // Prevent concurrent calls (only when called directly, not from rateSetData)
        if (this.isLoading) {
          console.log(
            'loadAvailabilityRates already in progress, skipping duplicate call',
          );
          return;
        }
        this.isLoading = true;
      }

      console.log('Loading availability rates...');
      console.log('rateSet.id:', this.rateSet.id);
      console.log('currentDate:', this.currentDate);

      const params = new URLSearchParams();
      if (this.rateSet && this.rateSet.id) {
        params.append('rate', this.rateSet.id);
      }
      if (this.currentDate) {
        params.append('date', this.currentDate);
      }
      const url = `/rates/load-availability-rates?${params.toString()}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        this.availabilityRates = data.availability_rates;
        this.sortAvailabilityRates(); // Sort after setting the data
      } catch (error) {
        console.error('Failed to fetch availability rates:', error);
        this.error = error.message;
      } finally {
        if (!skipLoadingState) {
          this.isInitializing = false;
          this.isLoading = false;
        }
      }
    },

    async rateSetData() {
      // Initial load: fetch both static data and availability rates
      // This is called once when the page loads
      this.isLoading = true;
      this.isInitializing = true;

      try {
        // Load static data first (needed for rateSet.id), then availability rates
        // We load static data first to ensure rateSet.id is available for loadAvailabilityRates
        await this.loadStaticData();
        // Pass skipLoadingState=true since we're managing loading state here
        await this.loadAvailabilityRates(true);
      } catch (error) {
        console.error('Failed to load rate settings data:', error);
        this.error = error.message;
      } finally {
        this.isInitializing = false;
        this.isLoading = false;
      }
    },
    updateRateSetField(field, value) {
      // Handle special case for parent field - convert empty string to null
      let processedValue = value;
      if (field === 'parent' && (value === '' || value === null)) {
        processedValue = null;
      }

      fetch(`/rates/update-rate-set/${this.rateSet.id}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify({ [field]: processedValue }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          if (data.success) {
            console.log('Rate updated successfully:', data);
            if (
              field === 'person_label_singular' ||
              field === 'person_label_plural'
            ) {
              this.normalizePersonLabels();
            }
            // Use the detailed message returned from the backend
            this.toastStore.addToast(data.message, 'success');
          } else {
            console.error('Failed to update RateSet:', data.errors);
            this.toastStore.addToast('Unsuccessfully Updated', 'error');
          }
        })
        .catch((error) => {
          console.error('Failed to update RateSet:', error);
          this.toastStore.addToast('Unsuccessfully Updated', 'error');
        });
    },
    normalizePersonLabels() {
      if (!this.rateSet) return;
      const singular = (this.rateSet.person_label_singular || '').trim();
      const plural = (this.rateSet.person_label_plural || '').trim();
      this.rateSet.person_label_singular = singular || 'Person';
      this.rateSet.person_label_plural = plural || 'People';
    },
    getPersonLabel(count, lowercase = false) {
      const label =
        Number(count) === 1
          ? this.rateSet?.person_label_singular || 'Person'
          : this.rateSet?.person_label_plural || 'People';
      return lowercase ? label.toLowerCase() : label;
    },
    updateRelatedRatesets(relatedRatesetIds) {
      fetch(`/rates/update-rate-set/${this.rateSet.id}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify({ related_ratesets: relatedRatesetIds }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          if (data.success) {
            console.log('Related ratesets updated successfully:', data);
            this.toastStore.addToast(data.message, 'success');
            // Update the local state
            this.rateSet.related_ratesets = relatedRatesetIds;
          } else {
            console.error('Failed to update related ratesets:', data.errors);
            this.toastStore.addToast(
              'Failed to update related services',
              'error',
            );
          }
        })
        .catch((error) => {
          console.error('Failed to update related ratesets:', error);
          this.toastStore.addToast(
            'Failed to update related services',
            'error',
          );
        });
    },
    updateRelatedStorePages(relatedStorePageIds) {
      fetch(`/rates/update-rate-set/${this.rateSet.id}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify({ related_store_pages: relatedStorePageIds }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          if (data.success) {
            console.log('Related store pages updated successfully:', data);
            this.toastStore.addToast(data.message, 'success');
            // Update the local state
            this.rateSet.related_store_pages = relatedStorePageIds;
          } else {
            console.error('Failed to update related store pages:', data.errors);
            this.toastStore.addToast(
              'Failed to update related services',
              'error',
            );
          }
        })
        .catch((error) => {
          console.error('Failed to update related store pages:', error);
          this.toastStore.addToast(
            'Failed to update related services',
            'error',
          );
        });
    },

    // 📅 Preset Duration CRUD methods
    async addPresetDuration(duration, name = '', isDefault = false) {
      try {
        const response = await fetch(
          `/rates/api/ratesets/${this.rateSet.id}/preset-durations/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              duration: duration,
              name: name,
              is_default: isDefault,
              order: this.rateSet.preset_durations.length,
            }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to add preset duration');
        }

        const data = await response.json();
        this.rateSet.preset_durations.push(data);
        this.toastStore.addToast('Preset duration added', 'success');
        return data;
      } catch (error) {
        console.error('Failed to add preset duration:', error);
        this.toastStore.addToast(
          error.message || 'Failed to add preset duration',
          'error',
        );
        throw error;
      }
    },

    async updatePresetDuration(presetId, updates) {
      try {
        const response = await fetch(
          `/rates/api/ratesets/${this.rateSet.id}/preset-durations/${presetId}/`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(updates),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || 'Failed to update preset duration',
          );
        }

        const data = await response.json();
        // Update local state
        const index = this.rateSet.preset_durations.findIndex(
          (p) => p.id === presetId,
        );
        if (index !== -1) {
          this.rateSet.preset_durations[index] = data;
        }
        this.toastStore.addToast('Preset duration updated', 'success');
        return data;
      } catch (error) {
        console.error('Failed to update preset duration:', error);
        this.toastStore.addToast(
          error.message || 'Failed to update preset duration',
          'error',
        );
        throw error;
      }
    },

    async deletePresetDuration(presetId) {
      try {
        const response = await fetch(
          `/rates/api/ratesets/${this.rateSet.id}/preset-durations/${presetId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );

        if (!response.ok && response.status !== 204) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || 'Failed to delete preset duration',
          );
        }

        // Update local state
        this.rateSet.preset_durations = this.rateSet.preset_durations.filter(
          (p) => p.id !== presetId,
        );
        this.toastStore.addToast('Preset duration deleted', 'success');
      } catch (error) {
        console.error('Failed to delete preset duration:', error);
        this.toastStore.addToast(
          error.message || 'Failed to delete preset duration',
          'error',
        );
        throw error;
      }
    },

    async setDefaultPresetDuration(presetId) {
      // Update the is_default flag - the backend model's save() method handles clearing other defaults
      await this.updatePresetDuration(presetId, { is_default: true });
      // Update local state to reflect the change
      this.rateSet.preset_durations.forEach((p) => {
        p.is_default = p.id === presetId;
      });
    },

    // Related ratesets methods
    async loadAvailableServices() {
      this.relatedServicesLoading = true;
      try {
        const response = await fetch(
          `/rates/get-available-ratesets/${this.calendarSelected}/`,
        );
        const data = await response.json();
        if (data.success) {
          this.availableRatesets = data.ratesets.filter(
            (rs) => rs.id != this.rateSet.id,
          );
          this.availableStorePages = data.store_pages || [];
          this.allAvailableItems = data.all_items.filter(
            (item) => item.id != this.rateSet.id,
          );
          this.allows6RelatedRatesets = data.allows_6_related_ratesets || false;
          // Populate filteredStorePages for location dropdown
          this.filteredStorePages = data.store_pages || [];
        }
      } catch (error) {
        console.error('Error loading available services:', error);
      } finally {
        this.relatedServicesLoading = false;
      }
    },
    toggleRelatedRateset(ratesetId) {
      const index = this.selectedRelatedRatesets.indexOf(ratesetId);
      let hasChanged = false;

      if (index > -1) {
        // Remove the rateset
        this.selectedRelatedRatesets.splice(index, 1);
        hasChanged = true;
      } else {
        // Check the total limit (ratesets + store pages)
        const maxLimit = this.allows6RelatedRatesets ? 6 : 3;
        const totalSelected =
          this.selectedRelatedRatesets.length +
          this.selectedRelatedStorePages.length;
        if (totalSelected < maxLimit) {
          // Add the rateset
          this.selectedRelatedRatesets.push(ratesetId);
          hasChanged = true;
        } else {
          // Show warning that limit is reached
          const limitText = this.allows6RelatedRatesets ? '6' : '3';
          this.toastStore.addToast(
            `You can only select up to ${limitText} related services total`,
            'warning',
          );
          return;
        }
      }

      // Only save if there was an actual change
      if (hasChanged) {
        this.saveRelatedRatesets();
      }
    },
    toggleRelatedStorePage(storePageId) {
      const index = this.selectedRelatedStorePages.indexOf(storePageId);
      let hasChanged = false;

      if (index > -1) {
        // Remove the store page
        this.selectedRelatedStorePages.splice(index, 1);
        hasChanged = true;
      } else {
        // Check the total limit (ratesets + store pages)
        const maxLimit = this.allows6RelatedRatesets ? 6 : 3;
        const totalSelected =
          this.selectedRelatedRatesets.length +
          this.selectedRelatedStorePages.length;
        if (totalSelected < maxLimit) {
          // Add the store page
          this.selectedRelatedStorePages.push(storePageId);
          hasChanged = true;
        } else {
          // Show warning that limit is reached
          const limitText = this.allows6RelatedRatesets ? '6' : '3';
          this.toastStore.addToast(
            `You can only select up to ${limitText} related services total`,
            'warning',
          );
          return;
        }
      }

      // Only save if there was an actual change
      if (hasChanged) {
        this.saveRelatedStorePages();
      }
    },
    isRelatedRatesetSelected(ratesetId) {
      return this.selectedRelatedRatesets.includes(ratesetId);
    },
    isRelatedStorePageSelected(storePageId) {
      return this.selectedRelatedStorePages.includes(storePageId);
    },
    saveRelatedRatesets() {
      this.updateRelatedRatesets([...this.selectedRelatedRatesets]);
    },
    saveRelatedStorePages() {
      this.updateRelatedStorePages([...this.selectedRelatedStorePages]);
    },
    preventWheelOnFocusedNumberInput(event) {
      const target = event.target;
      if (
        !target ||
        !target.matches('input[type="number"], input[inputmode="decimal"]') ||
        target !== document.activeElement
      ) {
        return;
      }
      const deltaX = Math.abs(event.deltaX);
      const deltaY = Math.abs(event.deltaY);
      if (deltaY > deltaX) {
        target.blur();
      }
    },
    trackFieldChange(rateId, field, value) {
      // Prepare the update data
      let updatedValue = value;

      // Handle price field specifically
      if (field === 'price') {
        if (value === null || value === undefined || value === '') {
          updatedValue = 0;
        } else {
          const unformattedPrice = this.unformatPrice(value);
          updatedValue = isNaN(unformattedPrice) ? 0 : unformattedPrice;
        }
      }

      // Handle min_length and max_length fields
      if (field === 'min_length' || field === 'max_length') {
        if (value === null || value === undefined || value === '') {
          updatedValue = null;
        } else {
          const parsedValue = parseInt(value, 10);
          updatedValue = isNaN(parsedValue) ? null : parsedValue;
        }
      }

      // Initialize rate object if doesn't exist
      if (!this.pendingChanges[rateId]) {
        this.pendingChanges[rateId] = {};
      }

      // Store the change
      this.pendingChanges[rateId][field] = updatedValue;
      this.hasPendingChanges = true;

      console.log(
        '📝 Tracked change - RateID:',
        rateId,
        'Field:',
        field,
        'Value:',
        updatedValue,
      );

      // Debug: Log which rate this corresponds to
      for (const rateRow of this.availabilityRates) {
        const matchingRate = rateRow.rates?.find((r) => r.id == rateId);
        if (matchingRate) {
          console.log(
            `   → Rate belongs to: "${rateRow.label}", Interval: ${matchingRate.date} to ${matchingRate.end_date}`,
          );
          break;
        }
      }
    },

    hasFieldChange(rateId, field) {
      return (
        this.pendingChanges[rateId] &&
        this.pendingChanges[rateId][field] !== undefined
      );
    },

    hasIntervalFieldChange(startDate, endDate, field) {
      // Check if ANY rate in the interval has a pending change for the given field
      // This is used for interval-level fields like instant_book_enabled and is_blocked
      for (const rateRow of this.availabilityRates) {
        const rate = this.getFixedRateForInterval(rateRow, startDate, endDate);
        if (rate && this.hasFieldChange(rate.id, field)) {
          return true;
        }
      }
      return false;
    },

    trackIntervalFieldChange(startDate, endDate, field, value) {
      // Track changes for ALL rates in a fixed interval (all person counts)
      // This is used for fields like instant_book_enabled and is_blocked that apply to the entire interval

      // Find all rates for this interval across all person counts
      for (const rateRow of this.availabilityRates) {
        const rate = this.getFixedRateForInterval(rateRow, startDate, endDate);
        if (rate) {
          // Track the change for this rate
          this.trackFieldChange(rate.id, field, value);
        }
      }
    },

    isDateAvailable(day) {
      // Check if this date is available for booking
      // Available = has rate with price > 0 and not blocked
      // For people_influence_price, ALL person counts must have valid rates
      if (!this.availabilityRates || this.availabilityRates.length === 0) {
        return false;
      }

      if (this.rateSet.people_influence_price && this.rateSet.people_per_unit) {
        // Check ALL person counts have valid rates
        for (const rateRow of this.availabilityRates) {
          // Get the people count from the first rate in this row
          const peopleCount = rateRow.rates[0]?.people;
          const rate = this.getRateForDate(rateRow, day, peopleCount);

          if (!rate) return false; // No rate for this person count = unavailable

          const hasPrice = rate.price && rate.price > 0;
          const notBlocked = !rate.is_blocked;

          if (!hasPrice || !notBlocked) {
            return false; // Any person count missing valid rate = unavailable
          }
        }
        return true; // All person counts have valid rates
      } else {
        // Original logic for non-per-person pricing
        const rateRow = this.availabilityRates[0];
        if (!rateRow) return false;

        const rate = this.getRateForDate(rateRow, day, null);

        if (!rate) return false; // No rate = unavailable

        const hasPrice = rate.price && rate.price > 0;
        const notBlocked = !rate.is_blocked;

        return hasPrice && notBlocked;
      }
    },

    isFixedIntervalAvailable(interval) {
      // Check if a fixed interval is available for booking
      // Available = has rate with price > 0 and not blocked
      // For people_influence_price, ALL person counts must have valid rates
      if (!interval) return false;

      if (!this.availabilityRates || this.availabilityRates.length === 0) {
        return false;
      }

      if (this.rateSet.people_influence_price && this.rateSet.people_per_unit) {
        // Check ALL person counts have valid rates for this interval
        for (const rateRow of this.availabilityRates) {
          const rate = this.getFixedRateForInterval(
            rateRow,
            interval.date,
            interval.end_date,
          );

          if (!rate) return false; // No rate for this person count = unavailable

          const hasPrice = rate.price && rate.price > 0;
          const notBlocked = !rate.is_blocked;

          if (!hasPrice || !notBlocked) {
            return false; // Any person count missing valid rate = unavailable
          }
        }
        return true; // All person counts have valid rates
      } else {
        // Original logic for non-per-person pricing
        const rateRow = this.availabilityRates[0];
        if (!rateRow) return false;

        const firstRate = this.getFixedRateForInterval(
          rateRow,
          interval.date,
          interval.end_date,
        );
        if (!firstRate) return false; // No rate = unavailable

        const hasPrice = firstRate.price && firstRate.price > 0;
        const notBlocked = !firstRate.is_blocked;

        return hasPrice && notBlocked;
      }
    },

    discardChanges() {
      this.pendingChanges = {};
      this.hasPendingChanges = false;
      // Only reload availability rates to reset displayed fields (static data hasn't changed)
      this.loadAvailabilityRates();
    },

    async saveAllChanges() {
      if (!this.hasPendingChanges) return;

      this.isLoading = true;

      try {
        const rateType = this.rateSet.accommodation_type
          ? 'Accommodation'
          : 'Tour';

        // Prepare batch data: separate creates and updates
        const creates = [];
        const updates = [];

        for (const [rateId, fields] of Object.entries(this.pendingChanges)) {
          if (rateId.startsWith('new-')) {
            // New rate - extract date and people from ID
            const parts = rateId.split('-');
            const date = `${parts[1]}-${parts[2]}-${parts[3]}`;
            const people = parts[4] === 'null' ? null : parseInt(parts[4], 10);

            creates.push({
              date,
              people,
              fields,
            });
          } else {
            // Existing rate update - convert rateId to integer
            updates.push({
              rate_id: parseInt(rateId, 10),
              fields,
            });
          }
        }

        console.log(
          'Batch save - creates:',
          creates.length,
          'updates:',
          updates.length,
        );

        // Make single batch API call
        const response = await fetch('/rates/batch-update-rates/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            rate_set_id: this.rateSet.id,
            rate_type: rateType,
            creates,
            updates,
          }),
        });

        const data = await response.json();

        if (!data.success) {
          console.error('Batch save failed:', data);
          throw new Error(data.message || 'Failed to save changes');
        }

        // Success!
        console.log('Batch save successful:', data);
        const totalChanges = data.created + data.updated;
        this.toastStore.addToast(
          `Successfully saved ${totalChanges} change(s)!`,
          'success',
        );

        // Clear pending changes and reload availability rates (static data hasn't changed)
        this.pendingChanges = {};
        this.hasPendingChanges = false;
        // Reset loading state before reloading rates
        this.isLoading = false;
        await this.loadAvailabilityRates();
      } catch (error) {
        console.error('Failed to save changes:', error);
        this.toastStore.addToast(
          'Failed to save changes: ' + error.message,
          'error',
        );
      } finally {
        this.isLoading = false;
      }
    },
    async submitBulkUpdate() {
      const store = Alpine.store('rateSettingsStore');
      console.log('Bulk Form:', store.bulkForm);
      // Perform basic validation
      if (!store.bulkForm.startDate || !store.bulkForm.endDate) {
        alert('Please select a date range.');
        return;
      }

      // Validate prices only if the price field is active
      if (store.bulkForm.price_active) {
        if (
          store.rateSet.people_influence_price &&
          !store.bulkForm.prices.length
        ) {
          alert('Please enter prices for each person.');
          return;
        }

        if (!store.rateSet.people_influence_price && !store.bulkForm.price) {
          alert('Please enter a price.');
          return;
        }
      }

      // Show that its loading
      this.isLoading = true;

      // Prepare the payload
      const payload = {
        start_date: store.bulkForm.startDate,
        end_date: store.bulkForm.endDate,
        price: store.bulkForm.price,
        prices: store.bulkForm.prices,
        min_length: store.bulkForm.min_length,
        max_length: store.bulkForm.max_length,
        closed_to_arrival: store.bulkForm.closed_to_arrival,
        closed_to_departure: store.bulkForm.closed_to_departure,
        instant_book_enabled: store.bulkForm.instant_book_enabled,
        is_blocked:
          store.bulkForm.is_blocked === true ||
          store.bulkForm.is_blocked === 'true',
        rateSetId: store.rateSet.id,
        people_influence_price: store.rateSet.people_influence_price,
        // Active fields
        price_active: store.bulkForm.price_active,
        min_length_active: store.bulkForm.min_length_active,
        max_length_active: store.bulkForm.max_length_active,
        closed_to_arrival_active: store.bulkForm.closed_to_arrival_active,
        closed_to_departure_active: store.bulkForm.closed_to_departure_active,
        instant_book_enabled_active: store.bulkForm.instant_book_enabled_active,
        is_blocked_active: store.bulkForm.is_blocked_active,
      };

      try {
        const response = await fetch(`/rates/bulk-update/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (data.success && data.task_id) {
          console.log('Bulk update task queued:', data);
          store.toastStore.addToast(
            'Bulk update is processing in the background. This may take a few moments.',
            'info',
          );

          // Keep loading state active - it will be cleared when task completes
          // Start polling for task status
          this.pollBulkUpdateStatus(data.task_id);
        } else if (data.success) {
          // Fallback for immediate success (backward compatibility)
          console.log('Bulk update successful:', data);
          store.toastStore.addToast('Bulk update successful!', 'success');
          store.clearBulkForm();
          // Reset loading state before reloading rates
          this.isLoading = false;
          // Only reload availability rates (static data hasn't changed)
          store.loadAvailabilityRates();
        } else {
          console.error('Failed to apply bulk update:', data.message);
          store.toastStore.addToast(
            `Bulk update failed: ${data.message}`,
            'error',
          );
          this.isLoading = false;
        }
      } catch (error) {
        console.error('Error applying bulk update:', error);
        store.toastStore.addToast('Bulk update failed.', 'error');
        this.isLoading = false;
      }
    },
    async pollBulkUpdateStatus(taskId) {
      const store = Alpine.store('rateSettingsStore');
      const maxAttempts = 120; // Poll for up to 10 minutes (5 sec intervals)
      let attempts = 0;

      const checkStatus = async () => {
        try {
          const response = await fetch(`/rates/bulk-update-status/${taskId}/`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const data = await response.json();
          console.log('Task status:', data);

          if (data.status === 'completed') {
            if (data.success) {
              store.toastStore.addToast(
                'Bulk update completed successfully!',
                'success',
              );
              // Clear the form after successful completion
              store.clearBulkForm();
              // Reset loading state before reloading rates
              store.isLoading = false;
              // Only reload availability rates (static data hasn't changed)
              await store.loadAvailabilityRates();
            } else {
              store.toastStore.addToast(
                `Bulk update completed with errors: ${data.message}`,
                'error',
              );
              store.isLoading = false;
            }
            return; // Stop polling
          } else if (data.status === 'failed') {
            store.toastStore.addToast(
              `Bulk update failed: ${data.message}`,
              'error',
            );
            store.isLoading = false;
            return; // Stop polling
          } else if (
            data.status === 'pending' ||
            data.status === 'in_progress'
          ) {
            // Task is still processing, continue polling
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 5000); // Poll every 5 seconds
            } else {
              store.toastStore.addToast(
                'Bulk update is taking longer than expected. Please refresh the page to check the results.',
                'warning',
              );
              store.isLoading = false;
            }
          }
        } catch (error) {
          console.error('Error checking task status:', error);
          // Continue polling despite errors, but limit attempts
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000);
          } else {
            // If we've exhausted attempts, stop loading
            store.isLoading = false;
          }
        }
      };

      // Start the polling
      checkStatus();
    },
    clearBulkForm() {
      // Reset the bulkForm data
      this.bulkForm = {
        startDate: null,
        endDate: null,
        price: null,
        prices: [],
        min_length: null,
        max_length: null,
        closed_to_arrival: [],
        closed_to_departure: [],
        instant_book_enabled: [],
        price_active: false,
        min_length_active: false,
        max_length_active: false,
        closed_to_arrival_active: false,
        closed_to_departure_active: false,
        instant_book_enabled_active: false,
        is_blocked_active: false,
        is_blocked: false,
      };

      // Clear the Flatpickr instance
      if (this.flatpickrInstance) {
        console.log('Reset the Flatpickr date picker...');
        this.flatpickrInstance.clear(); // Reset the Flatpickr date picker
      }
    },
    // Initialize Flatpickr for the single date picker
    initializeSingleDatePicker(element) {
      this.singleDatePickerInstance = flatpickr(element, {
        dateFormat: 'Y-m-d', // Internal format (for storing and sending)
        altInput: true, // Enables alternate input for displaying the user-friendly format
        altFormat: 'd-M', // Display format (for users) - Short format like "11-Oct"
        showMonths: 2, // Show two months for better date selection
        defaultDate: this.currentDate || '', // Set the current date as the default date if available
        onChange: (selectedDates) => {
          // Skip if this is a programmatic change (from navigation buttons)
          if (this.isProgrammaticDateChange) {
            return;
          }

          if (selectedDates.length > 0) {
            this.tempDate = formatDateISO(selectedDates[0]); // Timezone-safe YYYY-MM-DD format
            this.updateCurrentDate(); // Update the currentDate after selecting a date
          }
        },
      });
    },
    // Flatpickr initialization
    initializeFlatpickr(element) {
      // Calculate date 2 years from today
      const today = new Date();
      const maxDate = new Date(today);
      maxDate.setFullYear(today.getFullYear() + 2);

      this.flatpickrInstance = flatpickr(element, {
        mode: 'range',
        dateFormat: 'd/m/Y',
        showMonths: 2, // Range picker always shows two months
        maxDate: maxDate,
        onOpen: (selectedDates, dateStr, instance) => {
          // Jump to the month currently being viewed in the table when calendar opens
          if (this.currentDate) {
            // Convert YYYY-MM-DD string to a Date object
            const dateToJump = parseDate(this.currentDate);
            if (dateToJump) {
              instance.jumpToDate(dateToJump);
            }
          }
        },
        onChange: (selectedDates) => {
          // Make sure two dates (start and end) are selected
          if (selectedDates.length === 2) {
            const [startDate, endDate] = selectedDates;
            // Convert to the desired format (YYYY-MM-DD) for consistency using local time
            this.bulkForm.startDate = formatDateISO(startDate); // Timezone-safe YYYY-MM-DD format
            this.bulkForm.endDate = formatDateISO(endDate);
          } else {
            // Reset if the range is not complete
            this.bulkForm.startDate = null;
            this.bulkForm.endDate = null;
          }
        },
      });
    },
    storeOriginalValue(event) {
      // Store the original value of the input when it gains focus
      const rawValue = event.target.value;
      this.originalValue = this.unformatPrice(rawValue);
      console.log(
        'Original value stored - raw:',
        rawValue,
        'unformatted:',
        this.originalValue,
      );
    },
    updateCurrentDate() {
      // Only update currentDate when a valid date is selected
      if (this.tempDate) {
        this.currentDate = this.tempDate;
        // Only reload availability rates (static data doesn't change)
        this.loadAvailabilityRates();
      }
    },
    // Method to handle activation/deactivation of bulk form fields
    updateFieldStatus(field) {
      if (field === 'prices' && !this.bulkForm.prices_active) {
        // If the field is inactive, set it to null
        this.bulkForm.prices = [];
        // Initialize the prices array if price is activated
        if (!this.bulkForm.prices || this.bulkForm.prices.length === 0) {
          this.bulkForm.prices = Array.from(
            { length: this.rateSet.people_per_unit },
            () => null,
          );
        }
      } else if (field === 'is_blocked' && !this.bulkForm.is_blocked_active) {
        // If the blocked field is inactive, set it to false
        this.bulkForm.is_blocked = false;
      } else if (!this.bulkForm[`${field}_active`]) {
        // If the field is inactive, set it to null
        this.bulkForm[field] = null;
      }

      // Ensure Alpine.js recognizes the changes
      this.bulkForm = {
        ...this.bulkForm,
      };
    },
    getDatesRange() {
      if (!this.currentDate) {
        console.warn('getDatesRange - No current date!');
        return [];
      }

      const range = [];
      const startDate = parseDate(this.currentDate);
      if (!startDate) return range;

      // Generate unique dates for 14 days
      for (let i = 0; i < 14; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const dateStr = formatDateISO(date);
        if (!range.includes(dateStr)) {
          // Prevent duplicates
          range.push(dateStr);
        }
      }

      return range;
    },
    navigatePrevious14Days() {
      // Move back 14 days
      if (!this.currentDate) return;

      const currentDate = parseDate(this.currentDate);
      if (!currentDate) return;
      currentDate.setDate(currentDate.getDate() - 14);
      this.currentDate = formatDateISO(currentDate);

      // Prevent date picker onChange from firing during programmatic update
      this.isProgrammaticDateChange = true;

      // Update the single date picker instance if it exists
      if (this.singleDatePickerInstance) {
        this.singleDatePickerInstance.setDate(this.currentDate, false);
      }

      // Reset flag after date picker processes the change
      setTimeout(() => {
        this.isProgrammaticDateChange = false;
      }, 50);

      // Only reload availability rates for the new date range (static data doesn't change)
      this.loadAvailabilityRates();
    },
    navigateNext14Days() {
      // Move forward 14 days
      if (!this.currentDate) return;

      const currentDate = parseDate(this.currentDate);
      if (!currentDate) return;
      currentDate.setDate(currentDate.getDate() + 14);
      this.currentDate = formatDateISO(currentDate);

      // Prevent date picker onChange from firing during programmatic update
      this.isProgrammaticDateChange = true;

      // Update the single date picker instance if it exists
      if (this.singleDatePickerInstance) {
        this.singleDatePickerInstance.setDate(this.currentDate, false);
      }

      // Reset flag after date picker processes the change
      setTimeout(() => {
        this.isProgrammaticDateChange = false;
      }, 50);

      // Only reload availability rates for the new date range (static data doesn't change)
      this.loadAvailabilityRates();
    },
    getYearsRange() {
      const years = [];
      let currentYear = null;
      this.getDatesRange().forEach((date) => {
        const parsed = parseDate(date);
        if (!parsed) return;
        const year = parsed.getFullYear();
        if (year !== currentYear) {
          years.push(year);
          currentYear = year;
        }
      });
      return years;
    },
    getYearGroups() {
      const yearGroups = [];
      const dates = this.getDatesRange();

      let currentYear = null;
      let currentYearGroup = { year: null, days: [] };

      dates.forEach((date) => {
        const parsed = parseDate(date);
        if (!parsed) return;
        const year = parsed.getFullYear();

        if (year !== currentYear) {
          if (currentYearGroup.days.length > 0) {
            yearGroups.push(currentYearGroup);
          }
          currentYear = year;
          currentYearGroup = { year: currentYear, days: [] };
        }
        currentYearGroup.days.push(date);
      });

      if (currentYearGroup.days.length > 0) {
        yearGroups.push(currentYearGroup);
      }

      return yearGroups;
    },
    getRateForDate(rateRow, day, people) {
      // Find the rate for the specific date and number of people
      const foundRate = rateRow.rates.find(
        (rate) => rate.date === day && rate.people === people,
      );

      return foundRate || null;
    },

    // Get rate for date or create a placeholder for new rate creation
    getRateForDateOrPlaceholder(rateRow, day, people) {
      const foundRate = this.getRateForDate(rateRow, day, people);

      if (foundRate) {
        return foundRate;
      }

      // Return a placeholder object for creating new rates
      // Use a special ID format to indicate this is a new rate
      return {
        id: `new-${day}-${people || 'null'}`,
        date: day,
        people: people,
        price: 0,
        min_length: 0,
        max_length: 0,
        closed_to_arrival: false,
        closed_to_departure: false,
        instant_book_enabled: true,
        is_placeholder: true, // Flag to identify placeholders
      };
    },

    // Helper method to find matching rate for fixed intervals by date range
    getFixedRateForInterval(rateRow, startDate, endDate) {
      if (!rateRow || !rateRow.rates) {
        console.warn('getFixedRateForInterval - Invalid rateRow:', rateRow);
        return null;
      }

      const matchingRate = rateRow.rates.find(
        (rate) => rate.date === startDate && rate.end_date === endDate,
      );

      if (!matchingRate) {
        console.warn(
          'getFixedRateForInterval - No matching rate found for interval:',
          startDate,
          'to',
          endDate,
          'in rateRow:',
          rateRow.label,
          'Available rates:',
          rateRow.rates.map((r) => `${r.date} to ${r.end_date}`),
        );
      }

      return matchingRate || null;
    },

    // Helper method to update fixed interval rate price
    updateFixedIntervalPrice(rateRowIndex, startDate, endDate, newPrice) {
      const rateRow = this.availabilityRates[rateRowIndex];
      if (!rateRow || !rateRow.rates) {
        console.warn('updateFixedIntervalPrice - Invalid rateRow:', rateRow);
        return;
      }

      const rateIndex = rateRow.rates.findIndex(
        (rate) => rate.date === startDate && rate.end_date === endDate,
      );

      if (rateIndex !== -1) {
        // Update the price using Vue/Alpine reactivity-friendly approach
        rateRow.rates[rateIndex] = {
          ...rateRow.rates[rateIndex],
          price: newPrice,
        };
        console.log(
          'updateFixedIntervalPrice - Updated rate:',
          rateRow.rates[rateIndex],
        );
      } else {
        console.warn(
          'updateFixedIntervalPrice - Rate not found for interval:',
          startDate,
          'to',
          endDate,
        );
      }
    },

    getRateForId(rateId, people = null) {
      // Logic to find the rateData based on rateId and people (if applicable)
      // This might be a lookup in the rates array using rateId and filtering by people if needed
      let rateData = this.availabilityRates.find((rate) => rate.id === rateId);
      if (rateData && people) {
        // Optionally filter further based on people count
        rateData = rateData.peopleData[people - 1]; // Adjust as needed depending on structure
      }
      return rateData;
    },
    toggleClosedToArrival(day, isChecked) {
      const index = this.bulkForm.closed_to_arrival.indexOf(day);
      if (isChecked && index === -1) {
        this.bulkForm.closed_to_arrival.push(day);
      } else if (!isChecked && index > -1) {
        this.bulkForm.closed_to_arrival.splice(index, 1);
      }
    },

    toggleClosedToDeparture(day, isChecked) {
      const index = this.bulkForm.closed_to_departure.indexOf(day);
      if (isChecked && index === -1) {
        this.bulkForm.closed_to_departure.push(day);
      } else if (!isChecked && index > -1) {
        this.bulkForm.closed_to_departure.splice(index, 1);
      }
    },

    toggleInstantBookEnabled(day, isChecked) {
      const index = this.bulkForm.instant_book_enabled.indexOf(day);
      if (isChecked && index === -1) {
        this.bulkForm.instant_book_enabled.push(day);
      } else if (!isChecked && index > -1) {
        this.bulkForm.instant_book_enabled.splice(index, 1);
      }
    },

    formatDate(dateString) {
      const date = parseDate(dateString);
      if (!date) return dateString || '';
      const day = date.getDate().toString().padStart(2, '0'); // Ensure day is always two digits
      const monthAbbreviations = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthAbbreviations[date.getMonth()]; // Get three-letter month abbreviation
      return `${day} ${month}`;
    },
    formatFixedDateRange(startDate, endDate) {
      const formatDate = (dateString) => {
        const date = parseDate(dateString);
        if (!date) return dateString || '';
        const day = date.getDate().toString().padStart(2, '0'); // Ensure day is always two digits
        const monthAbbreviations = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ];
        const month = monthAbbreviations[date.getMonth()]; // Get three-letter month abbreviation
        const year = date.getFullYear(); // Get full year
        return `${day} ${month} ${year}`;
      };

      // If accommodation_type is true, adjust endDate by adding 1 day
      if (this.rateSet.accommodation_type) {
        const adjustedEndDate = parseDate(endDate);
        if (adjustedEndDate) {
          adjustedEndDate.setDate(adjustedEndDate.getDate() + 1); // Increment by 1 day
          endDate = formatDateISO(adjustedEndDate); // Update endDate to reflect next day
        }
      }

      // Format both start and end dates
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },
    initializeFixedIntervalDatepicker(el) {
      // Initialize a date range picker on the element
      // You might use a library like flatpickr for this
    },
    async submitNewFixedInterval() {
      // Validate input
      if (!this.newFixedInterval.dateRange) {
        alert('Please select a date range');
        return;
      }

      if (this.rateSet.people_influence_price) {
        if (
          this.newFixedInterval.prices.some(
            (price) => price === null || price === '',
          )
        ) {
          alert('Please fill in all price fields');
          return;
        }
      } else if (!this.newFixedInterval.price) {
        alert('Please enter a price');
        return;
      }

      // Parse the date range and convert to YYYY-MM-DD format
      const [startDate, endDate] = this.newFixedInterval.dateRange
        .split(' to ')
        .map((date) => {
          const [day, month, year] = date.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        });

      // Prepare the payload
      const payload = {
        start_date: startDate,
        end_date: endDate,
        rateSetId: this.rateSet.id,
        people_influence_price: this.rateSet.people_influence_price,
      };

      if (this.rateSet.people_influence_price) {
        payload.prices = this.newFixedInterval.prices.map((price) =>
          parseFloat(price),
        );
      } else {
        payload.price = parseFloat(this.newFixedInterval.price);
      }

      // Show that it's loading
      this.isLoading = true;

      try {
        const response = await fetch(`/rates/add-fixed-interval/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (data.success) {
          console.log('New fixed interval added successfully:', data);
          this.toastStore.addToast(
            'New fixed interval added successfully!',
            'success',
          );

          // Reset the form and reload data
          this.newFixedInterval.dateRange = '';
          this.newFixedInterval.price = null;
          this.newFixedInterval.prices = Array.from(
            { length: this.rateSet.people_per_unit },
            () => null,
          );

          // Reload the rates data
          await this.rateSetData(); // This will also call sortAvailabilityRates
        } else {
          console.error('Failed to add new fixed interval:', data.message);
          let errorMessage = 'Failed to add new fixed interval.';

          // Check for specific error types
          if (
            data.message.includes(
              'duplicate key value violates unique constraint',
            )
          ) {
            errorMessage = 'These fixed dates already exist.';
          } else if (data.message) {
            // Use the server's error message if available, but limit its length
            errorMessage =
              data.message.length > 100
                ? data.message.substring(0, 100) + '...'
                : data.message;
          }

          this.toastStore.addToast(errorMessage, 'error');
        }
      } catch (error) {
        console.error('Error adding new fixed interval:', error);
        this.toastStore.addToast(
          'Failed to add new fixed interval. Please try again.',
          'error',
        );
      } finally {
        this.isLoading = false;
      }
    },
    sortAvailabilityRates() {
      this.availabilityRates.forEach((availabilityRate) => {
        availabilityRate.rates.sort((a, b) => {
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          return (dateA ? dateA.getTime() : 0) - (dateB ? dateB.getTime() : 0);
        });
      });
    },
    async deleteFixedInterval(event, rateIndex) {
      if (!confirm('Are you sure you want to delete this fixed interval?')) {
        return;
      }

      this.isLoading = true;

      // Get the row that contains all the rates for this interval
      const row = event.target.closest('tr');

      // Collect all rate IDs for this interval from the data-id attributes
      const rateIds = Array.from(row.querySelectorAll('input[data-id]')).map(
        (input) => input.getAttribute('data-id'),
      );

      try {
        const response = await fetch('/rates/delete-fixed-interval/', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({ rateIds }),
        });

        const data = await response.json();

        if (data.success) {
          console.log('Fixed interval deleted successfully:', data);
          this.toastStore.addToast(
            'Fixed interval deleted successfully!',
            'success',
          );

          // Remove the deleted rates from the local data
          this.availabilityRates.forEach((rateRow) => {
            rateRow.rates.splice(rateIndex, 1);
          });

          // If all rates are deleted, reset the availabilityRates
          if (this.availabilityRates[0].rates.length === 0) {
            this.availabilityRates = [
              {
                label: 'Price',
                rates: [],
              },
            ];
          }
        } else {
          console.error('Failed to delete fixed interval:', data.message);
          this.toastStore.addToast(
            `Failed to delete fixed interval: ${data.message}`,
            'error',
          );
        }
      } catch (error) {
        console.error('Error deleting fixed interval:', error);
        this.toastStore.addToast('Failed to delete fixed interval.', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    formattedPrice(price) {
      if (
        price === null ||
        price === undefined ||
        isNaN(price) ||
        price === 0
      ) {
        return '';
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.currency,
      }).format(price);
    },
    // Helper function to remove currency formatting
    unformatPrice(formattedPrice) {
      if (typeof formattedPrice !== 'string') {
        return formattedPrice;
      }
      const numericString = formattedPrice.replace(/[^\d.-]/g, '');
      return numericString === '' ? 0 : parseFloat(numericString);
    },
    getCurrencySymbol() {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency').value;
    },
    addTaxFee(taxFeeId) {
      if (!taxFeeId) return; // Exit if no id is provided

      const taxFeeToAdd =
        this.availableTaxes.find((t) => t.id === parseInt(taxFeeId)) ||
        this.availableFees.find((f) => f.id === parseInt(taxFeeId));

      if (!taxFeeToAdd) {
        console.error('Tax/Fee not found');
        return;
      }

      this.isLoading = true;

      fetch(`/rates/add-tax-fee/${this.rateSet.id}/${taxFeeId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // Add the tax/fee to the local state
            if (!this.rateSet.tax_fees) {
              this.rateSet.tax_fees = [];
            }
            this.rateSet.tax_fees.push(taxFeeToAdd);
            this.toastStore.addToast('Tax/Fee added successfully', 'success');
          } else {
            throw new Error(data.error || 'Failed to add tax/fee');
          }
        })
        .catch((error) => {
          console.error('Error adding tax/fee:', error);
          this.toastStore.addToast('Failed to add tax/fee', 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    removeTaxFee(taxFeeId) {
      this.isLoading = true;
      fetch(`/rates/remove-tax-fee/${this.rateSet.id}/${taxFeeId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // Remove the tax/fee from the local state
            this.rateSet.tax_fees = this.rateSet.tax_fees.filter(
              (tf) => tf.id !== taxFeeId,
            );
            this.toastStore.addToast('Tax/Fee removed successfully', 'success');
          } else {
            throw new Error(data.error || 'Failed to remove tax/fee');
          }
        })
        .catch((error) => {
          console.error('Error removing tax/fee:', error);
          this.toastStore.addToast('Failed to remove tax/fee', 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    openNewTaxFeeModal() {
      this.showNewTaxFeeModal = true;
    },

    closeNewTaxFeeModal() {
      this.showNewTaxFeeModal = false;
      this.resetNewTaxFeeForm();
    },

    resetNewTaxFeeForm() {
      this.newTaxFee = {
        name: '',
        amount: 0,
        type: 'PERCENTAGE',
        is_tax: true,
        is_inclusive: false,
        applies_to_fees: false,
      };
    },

    async submitNewTaxFee() {
      try {
        // Add the selected calendar ID to the newTaxFee object
        this.newTaxFee.calendar_id = this.calendarSelected;

        // Ensure fixed fees are not inclusive
        const taxFeeData = {
          ...this.newTaxFee,
          is_inclusive:
            this.newTaxFee.type === 'PERCENTAGE'
              ? this.newTaxFee.is_inclusive
              : false,
          applies_to_fees: this.newTaxFee.is_tax
            ? this.newTaxFee.applies_to_fees
            : false,
        };

        const response = await fetch('/rates/create-tax-fee/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify(taxFeeData),
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Failed to create new tax/fee');
        }

        // Add the new tax/fee to the appropriate list
        const newTaxFee = result.tax_fee;
        if (newTaxFee.is_tax) {
          this.availableTaxes.push(newTaxFee);
        } else {
          this.availableFees.push(newTaxFee);
        }

        // Automatically add the new tax/fee to the rate set
        await this.addTaxFee(newTaxFee.id);

        this.toastStore.addToast(
          result.message || 'New tax/fee created and applied successfully',
          'success',
        );
        this.closeNewTaxFeeModal();
      } catch (error) {
        console.error('Error creating new tax/fee:', error);
        this.toastStore.addToast(
          error.message || 'Failed to create new tax/fee',
          'error',
        );
      }
    },

    // Discount management methods
    addDiscount(discountId) {
      if (!discountId) return; // Exit if no id is provided

      const discountToAdd = this.availableDiscounts.find(
        (d) => String(d.id) === String(discountId),
      );

      if (!discountToAdd) {
        console.error('Discount not found');
        return;
      }

      this.isLoading = true;

      fetch(`/rates/add-discount/${this.rateSet.id}/${discountId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // Add the discount to the local state
            if (!this.rateSet.discounts) {
              this.rateSet.discounts = [];
            }
            this.rateSet.discounts.push(discountToAdd);
            this.toastStore.addToast('Discount added successfully', 'success');
          } else {
            throw new Error(data.error || 'Failed to add discount');
          }
        })
        .catch((error) => {
          console.error('Error adding discount:', error);
          this.toastStore.addToast('Failed to add discount', 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    removeDiscount(discountId) {
      this.isLoading = true;
      fetch(`/rates/remove-discount/${this.rateSet.id}/${discountId}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // Remove the discount from the local state
            this.rateSet.discounts = this.rateSet.discounts.filter(
              (d) => String(d.id) !== String(discountId),
            );
            this.toastStore.addToast(
              'Discount removed successfully',
              'success',
            );
          } else {
            throw new Error(data.error || 'Failed to remove discount');
          }
        })
        .catch((error) => {
          console.error('Error removing discount:', error);
          this.toastStore.addToast('Failed to remove discount', 'error');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Helper method to get date period description (same as in calendarSettingsStore)
    getDatePeriodDescription(startDate, endDate, isRecurring) {
      if (!startDate || !endDate) return 'Always active';

      const start = parseDate(startDate);
      const end = parseDate(endDate);
      if (!start || !end) return 'Always active';

      // Check if period is longer than a year
      const oneYear = 365 * 24 * 60 * 60 * 1000; // milliseconds in a year
      const isLongerThanYear = end - start > oneYear;

      if (isLongerThanYear) {
        return isRecurring
          ? 'Always active (period > 1 year)'
          : `${start.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })} - ${end.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })} (one-time)`;
      }

      if (isRecurring) {
        // For recurring, show month/day only since it repeats yearly
        const startStr = start.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        const endStr = end.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        return `${startStr} - ${endStr} (recurring yearly)`;
      } else {
        // For one-time, show the full date with year
        const startStr = start.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        const endStr = end.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        return `${startStr} - ${endStr} (one-time)`;
      }
    },

    getWeekdaysDisplay(weekdays) {
      if (!weekdays || weekdays.length === 0) return '';

      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const selectedDays = weekdays
        .map((w) => Number(w))
        .sort((a, b) => a - b)
        .map((w) => dayNames[w]);

      // If all days selected, show "All days"
      if (selectedDays.length === 7) return 'All days';

      // If consecutive days, show range (e.g., "Mon-Thu")
      if (this.isConsecutiveDays(weekdays)) {
        return `${selectedDays[0]}-${selectedDays[selectedDays.length - 1]}`;
      }

      // Otherwise show comma-separated list
      return selectedDays.join(', ');
    },

    isConsecutiveDays(weekdays) {
      if (!weekdays || weekdays.length < 2) return false;

      const sorted = weekdays.map((w) => Number(w)).sort((a, b) => a - b);

      // Check if days are consecutive (accounting for week wrap-around)
      for (let i = 1; i < sorted.length; i++) {
        const diff = sorted[i] - sorted[i - 1];
        if (diff !== 1 && !(sorted[i] === 0 && sorted[i - 1] === 6)) {
          return false;
        }
      }
      return true;
    },

    // Add-ons management methods
    async loadAvailableAddons() {
      this.addonsLoading = true;
      try {
        const response = await fetch(
          `/calendar/get-available-addons/${this.calendarSelected}/`,
        );
        const data = await response.json();
        if (data.success) {
          this.availableAddons = data.addons || [];
        } else {
          console.error('Add-ons API returned error:', data);
        }
      } catch (error) {
        console.error('Error loading available add-ons:', error);
      } finally {
        this.addonsLoading = false;
      }
    },

    toggleAddon(addonId) {
      const index = this.selectedAddons.indexOf(addonId);
      let hasChanged = false;

      if (index > -1) {
        // Remove the addon
        this.selectedAddons.splice(index, 1);
        hasChanged = true;
      } else {
        // Add the addon
        this.selectedAddons.push(addonId);
        hasChanged = true;
      }

      // Only save if there was an actual change
      if (hasChanged) {
        this.saveAddons();
      }
    },

    isAddonSelected(addonId) {
      return this.selectedAddons.includes(addonId);
    },

    saveAddons() {
      this.updateAddons([...this.selectedAddons]);
    },

    updateAddons(addonIds) {
      fetch(`/rates/update-rate-set/${this.rateSet.id}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify({ associated_addons: addonIds }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((data) => {
          if (data.success) {
            console.log('Add-ons updated successfully:', data);
            this.toastStore.addToast(data.message, 'success');
            // Update the local state
            this.rateSet.associated_addons = addonIds;
          } else {
            console.error('Failed to update add-ons:', data.errors);
            this.toastStore.addToast('Failed to update add-ons', 'error');
          }
        })
        .catch((error) => {
          console.error('Failed to update add-ons:', error);
          this.toastStore.addToast('Failed to update add-ons', 'error');
        });
    },

    // Package structure management methods
    async loadPackageComponents() {
      this.packageLoading = true;
      try {
        const res = await fetch(
          `/rates/api/package-components/${this.rateSet.id}/`,
        );
        const data = await res.json();
        const normalizedComponents = (data.components || []).map(
          (component) => {
            const mode = component.date_application_mode || 'RANGE';
            const maxDaysMap =
              component.max_days_primary_duration_map &&
              typeof component.max_days_primary_duration_map === 'object'
                ? component.max_days_primary_duration_map
                : {};
            const maxDaysRows = Object.entries(maxDaysMap)
              .map(([primaryDays, maxDays]) => ({
                primary_days: Number(primaryDays),
                max_days: Number(maxDays),
              }))
              .filter(
                (row) =>
                  Number.isInteger(row.primary_days) &&
                  row.primary_days >= 1 &&
                  Number.isInteger(row.max_days) &&
                  row.max_days >= 0,
              )
              .sort((a, b) => a.primary_days - b.primary_days);
            return {
              ...component,
              original_revenue_allocation_percentage:
                parseFloat(component.revenue_allocation_percentage) || 0,
              date_application_mode: mode,
              adding_direction: component.adding_direction || 'FROM_START',
              max_days_mode: component.max_days_mode || 'FIXED',
              max_days_primary_duration_map: maxDaysMap,
              max_days_primary_duration_rows:
                (component.max_days_mode || 'FIXED') === 'PRIMARY_DURATION_MAP'
                  ? maxDaysRows.length > 0
                    ? maxDaysRows
                    : [{ primary_days: null, max_days: null }]
                  : maxDaysRows,
              min_primary_interval_days:
                component.min_primary_interval_days || null,
              weekdays: Array.isArray(component.weekdays)
                ? component.weekdays
                : [],
              weekdayRestrictionActive:
                Array.isArray(component.weekdays) &&
                component.weekdays.length > 0,
            };
          },
        );
        this.packageComponents = normalizedComponents;
        this.primaryComponent = normalizedComponents.find((c) => c.is_primary);
        this.secondaryComponents = normalizedComponents.filter(
          (c) => !c.is_primary,
        );
        // Calculate total revenue allocation after loading components
        if (this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED') {
          this.calculateTotalRevenue();
        }
      } catch (error) {
        console.error('Error loading package components:', error);
        this.toastStore.addToast('Failed to load package components', 'error');
      } finally {
        this.packageLoading = false;
      }
    },

    async loadPackageExtras() {
      try {
        const res = await fetch(
          `/rates/api/package-extras/${this.rateSet.id}/`,
        );
        const data = await res.json();
        this.packageExtras = (data.extras || []).map((extra) => {
          const withOriginal = {
            ...extra,
            original_revenue_allocation_percentage:
              parseFloat(extra.revenue_allocation_percentage) || 0,
          };
          if (
            extra.quantity_type === 'PER_DAY' ||
            extra.quantity_type === 'PER_NIGHT'
          ) {
            return { ...withOriginal, quantity_type: 'PER_PRIMARY_DURATION' };
          }
          return withOriginal;
        });
      } catch (error) {
        console.error('Error loading package extras:', error);
        this.toastStore.addToast('Failed to load package extras', 'error');
      }
    },

    async loadAvailablePackageExtras() {
      try {
        const response = await fetch(
          `/calendar/get-available-extras/${this.calendarSelected}/`,
        );
        if (!response.ok) {
          throw new Error('Failed to fetch extras');
        }
        const data = await response.json();
        this.availablePackageExtras = data.extras || [];
      } catch (error) {
        console.error('Error loading available package extras:', error);
        this.toastStore.addToast('Failed to load available extras', 'error');
      }
    },

    getSelectablePackageComponents() {
      const selected = new Set(
        [
          this.primaryComponent?.component_rate_set?.id,
          ...(this.secondaryComponents || []).map(
            (item) => item.component_rate_set?.id,
          ),
        ]
          .filter(Boolean)
          .map((id) => String(id)),
      );

      return (this.availableRatesets || []).filter((rateSet) => {
        const id = String(rateSet.id);
        const isCurrentRateSet = String(this.rateSet.id) === id;
        const isAlreadySelected = selected.has(id);
        const isTour = Boolean(rateSet.tour_type);
        const isPackage = Boolean(rateSet.is_package);
        const isFixed = Boolean(rateSet.is_fixed);
        return (
          !isCurrentRateSet &&
          !isAlreadySelected &&
          isTour &&
          !isPackage &&
          !isFixed
        );
      });
    },

    getSelectablePackageExtras() {
      const selected = new Set(
        (this.packageExtras || []).map((item) => String(item.extra.id)),
      );
      return (this.availablePackageExtras || []).filter(
        (extra) => !selected.has(String(extra.id)),
      );
    },

    async addPackageComponent(componentRateSetId) {
      try {
        const response = await fetch(
          `/rates/api/package-components/${this.rateSet.id}/create/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              component_rate_set_id: componentRateSetId,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to add package component');
        }

        await this.loadPackageComponents();
        if (this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED') {
          this.calculateTotalRevenue();
        }
        this.toastStore.addToast('Secondary component added', 'success');
      } catch (error) {
        console.error('Error adding package component:', error);
        this.toastStore.addToast(
          error.message || 'Failed to add package component',
          'error',
        );
      }
    },

    async addPackageExtra(extraId) {
      try {
        const response = await fetch(
          `/rates/api/package-extras/${this.rateSet.id}/create/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({ extra_id: extraId }),
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to add package extra');
        }

        await this.loadPackageExtras();
        this.toastStore.addToast('Package extra added', 'success');
      } catch (error) {
        console.error('Error adding package extra:', error);
        this.toastStore.addToast('Failed to add package extra', 'error');
      }
    },

    async updatePackageExtra(component) {
      try {
        this.calculateTotalRevenue();
        if (
          this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED' &&
          this.totalRevenueAllocation > 100.01
        ) {
          this.toastStore.addToast(
            `Total revenue allocation cannot exceed 100% (currently ${this.totalRevenueAllocation.toFixed(
              2,
            )}%).`,
            'error',
          );
          return;
        }

        const payload = {
          quantity_type: component.quantity_type,
          fixed_quantity: component.fixed_quantity,
          max_quantity: component.max_quantity,
          min_primary_interval_days: component.min_primary_interval_days,
          duration_adjustment: component.duration_adjustment,
          revenue_allocation_percentage:
            this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED'
              ? component.revenue_allocation_percentage ?? 0
              : 0,
          pricing_mode: component.pricing_mode,
          discount_type: component.discount_type,
          discount_amount:
            component.pricing_mode === 'DISCOUNTED'
              ? component.discount_amount
              : 0,
          shows_on_breakdown: component.shows_on_breakdown,
          display_name: component.display_name,
          active: component.active,
          order: component.order,
        };

        const response = await fetch(
          `/rates/api/package-extra/${component.id}/update/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(payload),
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to update package extra');
        }
        await this.loadPackageExtras();
        this.toastStore.addToast('Package extra updated', 'success');
      } catch (error) {
        console.error('Error updating package extra:', error);
        this.toastStore.addToast('Failed to update package extra', 'error');
      }
    },

    async deletePackageComponent(componentId) {
      try {
        const allocationMessage =
          this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED'
            ? '\n\nIts revenue allocation will be automatically returned to the primary component.'
            : '';
        const confirmed = window.confirm(
          `Are you sure you want to remove this secondary component?${allocationMessage}`,
        );
        if (!confirmed) {
          return;
        }

        const response = await fetch(
          `/rates/api/package-component/${componentId}/delete/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to delete package component');
        }

        await this.loadPackageComponents();
        if (this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED') {
          this.calculateTotalRevenue();
        }
        this.toastStore.addToast('Secondary component removed', 'success');
      } catch (error) {
        console.error('Error deleting package component:', error);
        this.toastStore.addToast(
          error.message || 'Failed to delete package component',
          'error',
        );
      }
    },

    async deletePackageExtra(componentId) {
      try {
        const allocationMessage =
          this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED'
            ? '\n\nIts revenue allocation will be automatically returned to the primary component.'
            : '';
        const confirmed = window.confirm(
          `Are you sure you want to remove this package extra?${allocationMessage}`,
        );
        if (!confirmed) {
          return;
        }

        const response = await fetch(
          `/rates/api/package-extra/${componentId}/delete/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        const data = await response.json();
        if (!response.ok || data.status !== 'success') {
          throw new Error(data.message || 'Failed to delete package extra');
        }
        await this.loadPackageExtras();
        this.toastStore.addToast('Package extra removed', 'success');
      } catch (error) {
        console.error('Error deleting package extra:', error);
        this.toastStore.addToast('Failed to delete package extra', 'error');
      }
    },

    isPrimaryComponentPersonBased() {
      if (!this.primaryComponent) {
        return false;
      }

      const rateSet = this.primaryComponent.component_rate_set;
      const unitType = rateSet.accommodation_type || rateSet.tour_type;

      if (!unitType) {
        return false;
      }

      // Check if unit type name is person-based
      const personBasedTypes = [
        'person',
        'people',
        'adult',
        'child',
        'adults',
        'children',
      ];
      const isPersonBased =
        personBasedTypes.some((type) =>
          unitType.toLowerCase().includes(type.toLowerCase()),
        ) || rateSet.people_as_option === true;

      return isPersonBased;
    },

    normalizePackageComponentDateRules(component) {
      const dateApplicationMode = component.date_application_mode || 'RANGE';
      const isRangeMode = dateApplicationMode === 'RANGE';

      if (!isRangeMode) {
        component.exclude_days_from_start = 0;
        component.exclude_days_from_end = 0;
        component.max_days = 0;
        component.max_days_mode = 'FIXED';
        component.max_days_primary_duration_map = {};
        component.max_days_primary_duration_rows = [];
        component.weekdays = [];
        component.weekdayRestrictionActive = false;
        component.adding_direction = 'FROM_START';
      } else if (!component.adding_direction) {
        component.adding_direction = 'FROM_START';
      }

      return component;
    },

    addMaxDaysPrimaryDurationRow(component) {
      if (!component.max_days_primary_duration_rows) {
        component.max_days_primary_duration_rows = [];
      }
      component.max_days_primary_duration_rows.push({
        primary_days: null,
        max_days: null,
      });
    },

    removeMaxDaysPrimaryDurationRow(component, rowIndex) {
      if (!component.max_days_primary_duration_rows) return;
      component.max_days_primary_duration_rows.splice(rowIndex, 1);
    },

    handleMaxDaysModeChange(component) {
      if (!component) return;
      if (component.max_days_mode === 'PRIMARY_DURATION_MAP') {
        if (!component.max_days_primary_duration_rows) {
          component.max_days_primary_duration_rows = [];
        }
        if (component.max_days_primary_duration_rows.length === 0) {
          this.addMaxDaysPrimaryDurationRow(component);
        }
      }
    },

    async updatePackageComponent(component) {
      try {
        this.normalizePackageComponentDateRules(component);
        this.calculateTotalRevenue();
        if (
          this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED' &&
          this.totalRevenueAllocation > 100.01
        ) {
          this.toastStore.addToast(
            `Total revenue allocation cannot exceed 100% (currently ${this.totalRevenueAllocation.toFixed(
              2,
            )}%).`,
            'error',
          );
          return;
        }

        // Validate: If PER_PERSON is selected but primary is not person-based, switch to FIXED
        if (
          component.quantity_type === 'PER_PERSON' &&
          !this.isPrimaryComponentPersonBased()
        ) {
          alert(
            'Cannot use "One per person" quantity type when the primary component is not person-based. Switching to "Fixed amount per booking".',
          );
          component.quantity_type = 'FIXED';
          if (!component.fixed_quantity || component.fixed_quantity < 1) {
            component.fixed_quantity = 1;
          }
        }

        // Prepare payload - set defaults for discount fields if not using discounts
        if (component.max_days_mode === 'PRIMARY_DURATION_MAP') {
          const rows = component.max_days_primary_duration_rows || [];
          const parsedMap = {};
          const seenDays = new Set();
          for (const row of rows) {
            const primaryDaysRaw = row?.primary_days;
            const maxDaysRaw = row?.max_days;
            const hasPrimary =
              primaryDaysRaw !== null &&
              primaryDaysRaw !== undefined &&
              primaryDaysRaw !== '';
            const hasMax =
              maxDaysRaw !== null &&
              maxDaysRaw !== undefined &&
              maxDaysRaw !== '';

            // Ignore fully empty rows
            if (!hasPrimary && !hasMax) {
              continue;
            }
            if (!hasPrimary || !hasMax) {
              this.toastStore.addToast(
                'Please fill both Primary days and Max days in each mapping row.',
                'error',
              );
              return;
            }

            const primaryDays = Number(primaryDaysRaw);
            const mappedMaxDays = Number(maxDaysRaw);
            if (!Number.isInteger(primaryDays) || primaryDays < 1) {
              this.toastStore.addToast(
                'Primary days must be a whole number >= 1.',
                'error',
              );
              return;
            }
            if (!Number.isInteger(mappedMaxDays) || mappedMaxDays < 0) {
              this.toastStore.addToast(
                'Max days must be a whole number >= 0.',
                'error',
              );
              return;
            }
            if (seenDays.has(primaryDays)) {
              this.toastStore.addToast(
                `Duplicate primary days value: ${primaryDays}.`,
                'error',
              );
              return;
            }
            seenDays.add(primaryDays);
            parsedMap[String(primaryDays)] = mappedMaxDays;
          }
          component.max_days_primary_duration_map = parsedMap;
        } else {
          component.max_days_primary_duration_map = {};
          component.max_days_primary_duration_rows = [];
        }

        const payload = {
          quantity_type: component.quantity_type,
          fixed_quantity: component.fixed_quantity,
          min_primary_interval_days: component.min_primary_interval_days,
          optional: component.optional,
          date_application_mode: component.date_application_mode || 'RANGE',
          adding_direction: component.adding_direction || 'FROM_START',
          exclude_days_from_start: component.exclude_days_from_start,
          exclude_days_from_end: component.exclude_days_from_end,
          // Weekdays are already stored as backend indices (0=Mon..6=Sun)
          weekdays: component.weekdays,
          max_days: component.max_days,
          max_days_mode: component.max_days_mode || 'FIXED',
          max_days_primary_duration_map:
            component.max_days_primary_duration_map || {},
          revenue_allocation_percentage:
            component.revenue_allocation_percentage,
          pricing_mode: component.pricing_mode,
          shows_on_breakdown: component.shows_on_breakdown,
          ignore_listing_blocked_dates:
            this.rateSet.package_pricing_mode === 'COMPONENT_SUM'
              ? !!component.ignore_listing_blocked_dates
              : false,
        };

        // Only include discount fields if using OWN_RATES_DISCOUNTED mode
        if (component.pricing_mode === 'OWN_RATES_DISCOUNTED') {
          payload.package_discount_type =
            component.package_discount_type || 'PERCENTAGE';
          payload.package_discount_amount =
            component.package_discount_amount || 0;
          // Conditional discount fields
          payload.package_discount_condition =
            component.package_discount_condition || 'ALWAYS';
          payload.package_discount_min_days =
            component.package_discount_min_days || null;
          payload.package_discount_max_days =
            component.package_discount_max_days || null;
          payload.package_discount_days_before =
            component.package_discount_days_before || null;
          payload.package_discount_weekdays =
            component.package_discount_weekdays || [];
        } else {
          // Set default values for non-discount modes
          payload.package_discount_type = 'PERCENTAGE';
          payload.package_discount_amount = 0;
          payload.package_discount_condition = 'ALWAYS';
          payload.package_discount_min_days = null;
          payload.package_discount_max_days = null;
          payload.package_discount_days_before = null;
          payload.package_discount_weekdays = [];
        }

        const response = await fetch(
          `/rates/api/package-component/${component.id}/update/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(payload),
          },
        );

        const data = await response.json();
        if (data.status === 'success') {
          // Reload components to get updated data
          await this.loadPackageComponents();
          this.toastStore.addToast(
            'Component updated successfully!',
            'success',
          );
          this.editingComponent = null;
        } else {
          this.toastStore.addToast('Error: ' + data.message, 'error');
        }
      } catch (error) {
        console.error('Error updating component:', error);
        this.toastStore.addToast('Failed to update component', 'error');
      }
    },

    // Revenue allocation management methods
    startEditingRevenue() {
      this.calculateTotalRevenue();
    },

    calculateTotalRevenue() {
      let total = 0;

      // Add primary component allocation
      if (
        this.primaryComponent &&
        this.primaryComponent.revenue_allocation_percentage
      ) {
        total +=
          parseFloat(this.primaryComponent.revenue_allocation_percentage) || 0;
      }

      // Add secondary components allocation
      this.secondaryComponents.forEach((comp) => {
        if (comp.revenue_allocation_percentage) {
          total += parseFloat(comp.revenue_allocation_percentage) || 0;
        }
      });

      // Add package extras allocation (PACKAGE_DEFINED only)
      if (this.rateSet.package_pricing_mode === 'PACKAGE_DEFINED') {
        this.packageExtras.forEach((extraComp) => {
          if (extraComp.active && extraComp.revenue_allocation_percentage) {
            total += parseFloat(extraComp.revenue_allocation_percentage) || 0;
          }
        });
      }

      this.totalRevenueAllocation = total;
    },

    // Toggle weekday for primary component's conditional discount
    togglePrimaryDiscountWeekday(dayIndex) {
      if (!this.primaryComponent) return;

      // Initialize array if not exists
      if (!this.primaryComponent.package_discount_weekdays) {
        this.primaryComponent.package_discount_weekdays = [];
      }

      const weekdays = this.primaryComponent.package_discount_weekdays;
      const index = weekdays.indexOf(dayIndex);

      if (index > -1) {
        // Remove the day
        weekdays.splice(index, 1);
      } else {
        // Add the day
        weekdays.push(dayIndex);
      }

      // Sort for consistency
      weekdays.sort((a, b) => a - b);
    },

    // Toggle weekday for a secondary component's conditional discount
    toggleComponentDiscountWeekday(component, dayIndex) {
      if (!component) return;

      // Initialize array if not exists
      if (!component.package_discount_weekdays) {
        component.package_discount_weekdays = [];
      }

      const weekdays = component.package_discount_weekdays;
      const index = weekdays.indexOf(dayIndex);

      if (index > -1) {
        // Remove the day
        weekdays.splice(index, 1);
      } else {
        // Add the day
        weekdays.push(dayIndex);
      }

      // Sort for consistency
      weekdays.sort((a, b) => a - b);
    },

    async saveAllRevenueAllocations() {
      // Validate total is 100%
      if (Math.abs(this.totalRevenueAllocation - 100) >= 0.01) {
        this.toastStore.addToast('Revenue allocation must total 100%', 'error');
        return;
      }

      try {
        const postJSON = async (url, payload) => {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data.status === 'error') {
            throw new Error(data.message || 'Failed to update allocation');
          }
        };

        const buildComponentPayload = (component) => {
          const payload = {
            revenue_allocation_percentage:
              component.revenue_allocation_percentage ?? 0,
          };
          if (component.pricing_mode === 'OWN_RATES_DISCOUNTED') {
            payload.package_discount_type =
              component.package_discount_type || 'PERCENTAGE';
            payload.package_discount_amount =
              component.package_discount_amount || 0;
          } else {
            payload.package_discount_type = 'PERCENTAGE';
            payload.package_discount_amount = 0;
          }
          return payload;
        };

        const getDelta = (item) =>
          (parseFloat(item.revenue_allocation_percentage) || 0) -
          (parseFloat(item.original_revenue_allocation_percentage) || 0);

        // Apply changes in a safe order to avoid temporary totals > 100:
        // decreases first, increases later, and primary handled based on its direction.
        const nonPrimaryUpdates = [
          ...this.secondaryComponents.map((component) => ({
            delta: getDelta(component),
            run: () =>
              postJSON(
                `/rates/api/package-component/${component.id}/update/`,
                buildComponentPayload(component),
              ),
          })),
          ...this.packageExtras.map((component) => ({
            delta: getDelta(component),
            run: () =>
              postJSON(`/rates/api/package-extra/${component.id}/update/`, {
                revenue_allocation_percentage:
                  component.revenue_allocation_percentage || 0,
              }),
          })),
        ].sort((a, b) => a.delta - b.delta);

        const primaryDelta = this.primaryComponent
          ? getDelta(this.primaryComponent)
          : 0;
        const runPrimaryUpdate = async () => {
          if (!this.primaryComponent) return;
          await postJSON(
            `/rates/api/package-component/${this.primaryComponent.id}/update/`,
            buildComponentPayload(this.primaryComponent),
          );
        };

        if (primaryDelta < 0) {
          await runPrimaryUpdate();
        }
        for (const update of nonPrimaryUpdates) {
          await update.run();
        }
        if (primaryDelta >= 0) {
          await runPrimaryUpdate();
        }

        await this.loadPackageComponents();
        await this.loadPackageExtras();
        this.toastStore.addToast(
          'Revenue allocations updated successfully!',
          'success',
        );
        this.editingRevenueAllocation = false;
      } catch (error) {
        console.error('Error updating revenue allocations:', error);
        this.toastStore.addToast(
          error.message || 'Failed to update revenue allocations',
          'error',
        );
      }
    },

    // Listing Custom Fields Methods
    async fetchListingFields() {
      try {
        const response = await fetch('/rates/api/custom-fields/definitions/', {
          method: 'GET',
          headers: {
            'X-CSRFToken': getCSRFToken(),
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          this.availableListingFields = await response.json();
        } else {
          console.error('Failed to fetch listing fields');
        }
      } catch (error) {
        console.error('Error fetching listing fields:', error);
      }
    },

    async fetchListingFieldAssignments() {
      if (!this.rateSet.id) return;

      try {
        const response = await fetch(
          `/rates/api/custom-fields/ratesets/${this.rateSet.id}/assignments/`,
          {
            method: 'GET',
            headers: {
              'X-CSRFToken': getCSRFToken(),
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.ok) {
          this.listingFieldAssignments = await response.json();
        } else {
          console.error('Failed to fetch listing field assignments');
        }
      } catch (error) {
        console.error('Error fetching listing field assignments:', error);
      }
    },

    isListingFieldAssigned(fieldId) {
      return this.listingFieldAssignments.some(
        (assignment) =>
          assignment.custom_field?.id === fieldId && assignment.is_active,
      );
    },

    getListingFieldAssignment(fieldId) {
      return this.listingFieldAssignments.find(
        (assignment) => assignment.custom_field?.id === fieldId,
      );
    },

    getActiveListingFieldCount() {
      return this.listingFieldAssignments.filter((assignment) =>
        this.countsTowardActiveListingFieldLimit(assignment),
      ).length;
    },

    countsTowardActiveListingFieldLimit(assignment) {
      return (
        assignment.is_active && assignment.custom_field?.is_active !== false
      );
    },

    canAddMoreListingFields(fieldId = null) {
      const field = fieldId
        ? this.availableListingFields.find((f) => f.id === fieldId)
        : null;
      if (field && field.is_active === false) {
        return true;
      }
      return this.getActiveListingFieldCount() < this.maxListingCustomFields;
    },

    toggleListingFieldAssignment(fieldId, isAssigned) {
      const existingAssignment = this.getListingFieldAssignment(fieldId);

      if (existingAssignment) {
        if (
          isAssigned &&
          !existingAssignment.is_active &&
          !this.canAddMoreListingFields(fieldId)
        ) {
          alert(
            `Maximum of ${this.maxListingCustomFields} custom fields per listing. Too many fields reduce conversion rates and make the booking process too complex for customers. Hightide is about keeping things simple for both businesses and customers.`,
          );
          return;
        }
        // Update existing assignment (always allow unchecking)
        existingAssignment.is_active = isAssigned;
      } else if (isAssigned) {
        if (!this.canAddMoreListingFields(fieldId)) {
          alert(
            `Maximum of ${this.maxListingCustomFields} custom fields per listing. Too many fields reduce conversion rates and make the booking process too complex for customers. Hightide is about keeping things simple for both businesses and customers.`,
          );
          return;
        }
        // Create new assignment
        const field = this.availableListingFields.find((f) => f.id === fieldId);
        if (field) {
          this.listingFieldAssignments.push({
            custom_field: field,
            is_active: true,
            is_required_override: null,
            custom_label_override: '',
            order_override: field.order,
          });
        }
      }
    },

    updateListingFieldRequirement(fieldId, isRequired) {
      const assignment = this.getListingFieldAssignment(fieldId);
      if (assignment) {
        assignment.is_required_override = isRequired;
      }
    },

    updateListingFieldOrder(fieldId, order) {
      const assignment = this.getListingFieldAssignment(fieldId);
      if (assignment) {
        // Use null if empty/0 to allow fallback to custom_field.order
        const parsedOrder = parseInt(order);
        assignment.order_override =
          parsedOrder && parsedOrder > 0 ? parsedOrder : null;
      }
    },

    updateListingFieldComponent(fieldId, componentRateSetId) {
      const assignment = this.getListingFieldAssignment(fieldId);
      if (assignment) {
        // Convert empty string to null for primary component
        assignment.component_rate_set =
          componentRateSetId === '' ? null : parseInt(componentRateSetId);
      }
    },

    async saveListingFieldAssignments() {
      if (!this.rateSet.id) return;

      this.isSavingListingFieldAssignments = true;

      try {
        // Save all assignments (both active and inactive) to allow reactivation
        const assignmentsToSave = this.listingFieldAssignments.filter(
          (assignment) => assignment.id || assignment.is_active,
        );

        for (const assignment of assignmentsToSave) {
          // Extract component_rate_set_id (handle both object and integer formats)
          const componentRateSetId =
            assignment.component_rate_set?.id ??
            assignment.component_rate_set ??
            null;

          // For packages, only validate component if assignment is active
          if (
            this.rateSet.is_package &&
            assignment.is_active &&
            !componentRateSetId
          ) {
            if (this.toastStore) {
              this.toastStore.addToast(
                `Please select a component for custom field "${assignment.custom_field.field_label}"`,
                'danger',
              );
            }
            this.isSavingListingFieldAssignments = false;
            return;
          }

          const data = {
            field_definition_id: assignment.custom_field.id,
            is_active: assignment.is_active,
            is_required_override: assignment.is_required_override,
            field_label_override: assignment.custom_label_override || '',
            order: assignment.order_override,
            component_rate_set_id: componentRateSetId,
          };

          const response = await fetch(
            `/rates/api/custom-fields/ratesets/${this.rateSet.id}/assignments/`,
            {
              method: 'POST',
              headers: {
                'X-CSRFToken': getCSRFToken(),
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(data),
            },
          );

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Failed to save assignment:', assignment, errorData);
            if (this.toastStore && errorData.error) {
              this.toastStore.addToast(errorData.error, 'danger');
            }
            this.isSavingListingFieldAssignments = false;
            return;
          }
        }

        // Remove assignments that were unchecked and never saved (no id)
        this.listingFieldAssignments = this.listingFieldAssignments.filter(
          (assignment) => assignment.is_active || assignment.id,
        );

        // Refresh assignments to get updated state
        await this.fetchListingFieldAssignments();

        if (this.toastStore) {
          this.toastStore.addToast(
            'Custom fields updated successfully!',
            'success',
          );
        }
      } catch (error) {
        console.error('Error saving listing field assignments:', error);
        if (this.toastStore) {
          this.toastStore.addToast(
            'Failed to save custom fields. Please try again.',
            'danger',
          );
        }
      } finally {
        this.isSavingListingFieldAssignments = false;
      }
    },
  });
}
